import { Component, EventEmitter, Inject, Injector, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BodyComponentObject, ButtonItemComponentObject, ButtonsComponentObject, FooterComponentObject, HeaderComponentObject, TemplateMessageObject } from '@trendbuild/trend-cloud-api';
import { ButtonActionsTypeEnum } from '@trendbuild/trend-z-api';
import { AbstractComponent, AlertModalComponent, applyWaTemplateParams, buildWaTemplateParams, Contact, ContactEditComponent, extractWaTemplateParameters, FirebaseService, MetadataMessage, resolveWaTemplateParameterFormat, UploadTypeEnum, UtilHelper, waTemplateButtonAction, waTemplateButtonLabel, WaTemplateParameterFormat, WhatsAppButton } from 'lib-trend-core';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'wa-template-message',
    // templateUrl: './wa-template-message.component.html',
    templateUrl: './wa-template-message.v2.component.html',
    styleUrls: ['./wa-template-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WaTemplateMessageComponent extends AbstractComponent implements OnInit {

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinnerTemplate$ = this.loadingSpinnerSubject.asObservable();

  @Input() set template(template: TemplateMessageObject) {
    this.selectedTemplate = template;

    this.extractTemplateVariables();
  }

  @Input() set contact(contact: Contact) {
    this.selectedContact = contact;
  }

  @Input() variables: Record<string, string>;

  @Output() templateParams = new EventEmitter();

  file: File;
  fileZero: any;
  fileZeroSrc: string;
  typeFile: string;

  selectedTemplate: TemplateMessageObject;
  selectedContact: Contact;

  /**
   * POSITIONAL or NAMED, as Meta records it on the template. Decides the shape of the params
   * emitted: an array matched by position, or an object keyed by placeholder name.
   */
  parameterFormat: WaTemplateParameterFormat = 'POSITIONAL';

  /** Placeholders of each component in order of appearance — `['1', '2']` or `['nome', 'pedido']`. */
  headerParameters: Array<string> = [];
  bodyParameters: Array<string> = [];

  ButtonActionsTypeEnum = ButtonActionsTypeEnum;

  previewText: string;
  previewFooter: string;
  previewHeader: any;

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ContactEditComponent>,
    private firebaseService: FirebaseService,
    private modalAlert: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: Contact,

  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
  }

  public get variablesFor() {
    return Object.keys(this.variables) ?? [];
  }

  public get headerComponent(): HeaderComponentObject {
    return <HeaderComponentObject>this.selectedTemplate?.components.find(c => c.type === 'HEADER');
  }

  public get bodyComponent(): BodyComponentObject {
    return <BodyComponentObject>this.selectedTemplate?.components.find(c => c.type === 'BODY');
  }

  public get footerComponent(): FooterComponentObject {
    return <FooterComponentObject>this.selectedTemplate?.components.find(c => c.type === 'FOOTER');
  }

  public get buttonsComponent(): ButtonsComponentObject {
    return <ButtonsComponentObject>this.selectedTemplate?.components.find(c => c.type === 'BUTTONS');
  }

  public get previewButtons(): Array<WhatsAppButton> {
    return (this.buttonsComponent?.buttons ?? []).map((button: ButtonItemComponentObject) => ({
      label: waTemplateButtonLabel(button),
      type: waTemplateButtonAction(button.type),
    }));
  }

  get headers() {
    return this.formGroup.controls['headers'] as FormArray;
  }

  get bodys() {
    return this.formGroup.controls['bodys'] as FormArray;
  }

  get headerValue() {
    return this.headers.controls[0].get('value').value;
  }

  private setupForm(): void {
    this.formGroup = new FormGroup({
      template: new FormControl(null, [Validators.required]),
      headers: new FormArray([]),
      bodys: new FormArray([]),
    });

    this.formGroup.valueChanges.subscribe((value) => {
      this.updatePreviewText();
      this.updateHeaderText();
      // A media header carries its link as the single value — never a name, whatever the format.
      const headerValues = this.headerValues;
      this.templateParams.emit(<MetadataMessage>{
        template: this.selectedTemplate,
        headerParams: this.isMedia ? headerValues : buildWaTemplateParams(this.parameterFormat, this.headerParameters, headerValues),
        bodyParams: buildWaTemplateParams(this.parameterFormat, this.bodyParameters, this.bodyValues),
        previewText: this.previewText,
      });
    })
  }

  private get headerValues(): Array<string> {
    return this.headers.controls.map((control: FormGroup) => control.get('value').value);
  }

  private get bodyValues(): Array<string> {
    return this.bodys.controls.map((control: FormGroup) => control.get('value').value);
  }

  private updateHeaderText() {
    if (this.headerComponent && !this.isMedia) {
      this.previewHeader = applyWaTemplateParams(this.headerComponent.text, this.headerParameters, this.headerValues);
    }
  }

  private updatePreviewText() {
    this.previewText = applyWaTemplateParams(this.bodyComponent?.text, this.bodyParameters, this.bodyValues);
  }

  /** How the variable is shown to the operator: its number for POSITIONAL, its `{{name}}` for NAMED. */
  parameterLabel(parameters: Array<string>, index: number): string {
    const parameter = parameters[index];
    return this.parameterFormat === 'NAMED' && !!parameter ? `{{${parameter}}}` : `${index + 1}`;
  }

  public get isMedia() {
    return (!!this.headerComponent && this.headerComponent!.format !== 'TEXT');
  }

  extractTemplateVariables(): void {
    (this.formGroup.get('headers') as FormArray).clear();
    (this.formGroup.get('bodys') as FormArray).clear();
    this.formGroup.get('template').setValue(this.selectedTemplate);

    // The variables come from the markers in the text, not from the example block: a NAMED template
    // reports its examples under `*_named_params`, so counting `body_text[0]` rendered no input at
    // all and the send left without parameters.
    this.parameterFormat = resolveWaTemplateParameterFormat(this.selectedTemplate);
    this.headerParameters = !!this.headerComponent && !this.isMedia ? extractWaTemplateParameters(this.headerComponent.text) : [];
    this.bodyParameters = extractWaTemplateParameters(this.bodyComponent?.text);

    if (this.headerComponent) {
      // A media header takes exactly one value (the link); a text header takes one per marker.
      const headerInputs = this.isMedia ? 1 : this.headerParameters.length;
      for (let i = 0; i < headerInputs; i++) {
        (this.formGroup.get('headers') as FormArray).push(
          new FormGroup({
            value: new FormControl(null, [Validators.required]),
          })
        );
      }
    }

    if (this.bodyComponent) {
      this.bodyParameters.forEach(() => {
        (this.formGroup.get('bodys') as FormArray).push(
          new FormGroup({
            value: new FormControl(null, [Validators.required]),
          })
        );
      });
      this.previewText = this.bodyComponent.text;
    }

    if (this.footerComponent) {
      this.previewFooter = this.footerComponent.text;
    }
  }

  updateValueForm(value: string, index: number, element: string) {
    (this.formGroup.controls[element] as FormArray).controls[index].get('value').setValue(value);
    this.updatePreviewText();
  }

  onSelectImagem(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files: FileList = target.files;
    this.file = files[0];

    if (this.file) {
      const fileType = this.file.type;
      const fileSize = this.file.size;
      const extensionFile = UtilHelper.getFileExtension(fileType);
      this.typeFile = UtilHelper.getMimeType(extensionFile);

      const fileTypeLimit = UtilHelper.getFileTypeLimit(fileType);
      if (fileTypeLimit) {
        if (fileSize > fileTypeLimit.maxSize) {
          this.openModalAlert(
            fileTypeLimit.alertTitle,
            fileTypeLimit.alertMessage,
            target
          )
          return;
        }
      } else {
        this.openModalAlert(this.t('attendance.waTemplateMessage.unsupportedFileType'), '', target);
        return;
      }

      let reader = new FileReader();
      reader.onload = async (file) => {
        if (this.file) {
          this.loadingSpinnerSubject.next(true);
          const contents = file.target as FileReader;
          const base64 = UtilHelper.arrayBufferToBase64(contents.result);
          const dateMilisecond = new Date().getTime();
          const filename: string = dateMilisecond.toString();
          this.firebaseService.uploadFile(this.file, filename, UploadTypeEnum.MESSAGE)
            .then((snapshot: { url: string }) => {
              this.loadingSpinnerSubject.next(false);
              this.headers.controls[0].get('value').setValue(snapshot.url);
            }).catch((error) => {
              this.loadingSpinnerSubject.next(false);
              console.error('Erro no upload da imagem:', error);
            });
        }
      };
      reader.readAsArrayBuffer(this.file);
    }
  }

  private openModalAlert(
    title: string,
    message: string,
    inputElement?: HTMLInputElement
  ): void {
    const dialogRefAlert = this.modalAlert.open(AlertModalComponent, {
      width: '500px',
      data: { title: title, message: message },
    })
    if (inputElement) {
      dialogRefAlert.afterClosed().subscribe(() => {
        inputElement.value = ''
      })
    }
  }

  acceptFiles(): string {
    let accept = '';
    if (this.isHeaderDocument()) {
      accept = '.pdf';
    } else if (this.isHeaderVideo()) {
      accept = '.mp4';
    } else if (this.isHeaderImage()) {
      accept = '.jpeg, .jpg, .png';
    }
    return accept;
  }

  isHeaderDocument() {
    return this.headerComponent.format === 'DOCUMENT';
  }

  isHeaderVideo() {
    return this.headerComponent.format === 'VIDEO';
  }

  isHeaderImage() {
    return this.headerComponent.format === 'IMAGE';
  }

  getIconForContentType(): string {
    let icon = '';
    if (this.isHeaderDocument()) {
      icon = UtilHelper.on_pdf;
    } else if (this.isHeaderVideo()) {
      icon = UtilHelper.on_video;
    }
    return icon;
  }

}